<template>
  <div class="hashtag-container">
    <div
      class="button-link"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      :class="{ 'hashtag-text-type': type === 'text' }"
      @click.stop
    >
      <template v-if="type === 'text'">
        <template v-for="item in items" :key="item.Id">
          <span class="tag-item" style="margin-left: 2px;">
            <span class="button-link__hashtag">
              <!-- <app-icon
                name="hash"
                width="16px"
                :color="disabled ? '#D2D2D2' : '#979797'"
                hover-color="#499259"
                cursor="pointer"
              /> -->
              #
            </span>
            <span class="tag-name">{{ item.Name }}</span>
          </span>
          <!-- <span v-if="index < items.length - 1" class="tag-separator"> </span> -->
        </template>
      </template>

      <template v-else>
        <app-resizable-dropdown
          v-if="selectorEnabled && !disabled"
          ref="dropdownRef"
          class="button-link__dropdown"
          :position="dropdownPosition"
          :close-by-click-outside="true"
          :max-width="dropdownMaxWidth"
          :max-height="dropdownMaxHeight"
          :mobile-title="$t('choose_tags')"
        >
          <template #activator>
            <span class="button-link__hashtag">
              <template v-if="!disabled">
                <app-tooltip :text="tooltipText">
                  <template #activator>
                    <app-icon
                      name="hash"
                      width="16px"
                      color="#979797"
                      hover-color="#499259"
                      cursor="pointer"
                    />
                  </template>
                </app-tooltip>
              </template>

              <template v-else>
                <app-tooltip :text="tooltipText">
                  <template #activator>
                    <app-icon
                      name="hash"
                      width="16px"
                      color="#D2D2D2"
                      cursor="pointer"
                    />
                  </template>
                </app-tooltip>
              </template>
            </span>
          </template>

          <app-tag-selector
            v-model="localSelectorValue"
            :entity-type="entityType"
            :mode="tagSelectorMode"
            :allow-create="allowCreate"
            :allow-select-group="allowSelectGroup"
            @update:model-value="handleSelectorChange"
            @create-tag="handleCreateTag"
            @close="dropdownRef?.close()"
          />
        </app-resizable-dropdown>

        <span v-else class="button-link__hashtag">
          <template v-if="!disabled">
            <app-tooltip :text="tooltipText">
              <template #activator>
                <app-icon
                  name="hash"
                  width="16px"
                  color="#979797"
                  hover-color="#499259"
                  cursor="pointer"
                />
              </template>
            </app-tooltip>
          </template>
          <template v-else>
            <app-tooltip :text="tooltipText">
              <template #activator>
                <app-icon
                  name="hash"
                  width="16px"
                  color="#D2D2D2"
                  cursor="pointer"
                />
              </template>
            </app-tooltip>
          </template>
        </span>

        <span class="button-link__wrapper">
          <template v-for="(item, index) in items" :key="item.Id">
            <span class="tag-item">
              <span class="tag-name" :class="{ 'tag-name-disabled': disabled }">
                {{ item.Name.toLowerCase() }}
              </span>

              <app-tooltip
                v-if="showClear"
                :text="$t('ui_remove_named', { name: item.Name.toLowerCase() })"
              >
                <template #activator>
                  <!-- <span
                    class="button-link__cross"
                    :class="{ 'button-link__cross-disabled': disabled }"
                    @click.stop="removeTag(item, index)"
                  >
                    ×
                  </span> -->
                  <app-icon
                    v-if="!disabled"
                    :class="{ 'button-link__cross-disabled': disabled }"
                    name="close-bold"
                    width="12px"
                    color="#D2D2D2"
                    hover-color="#499259"
                    cursor="pointer"
                    @click.stop="removeTag(item, index)"
                  />
                </template>
              </app-tooltip>
            </span>

            <!-- <span v-if="index < items.length - 1" class="tag-separator"> </span> -->
          </template>
        </span>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType } from "vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppIcon from "../icon/AppIcon.vue";
import AppResizableDropdown from "../dropdown/AppResizableDropdown.vue";
import AppTagSelector from "../tag-selector/AppTagSelector.vue";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { TagsApi } from "@/ api/TagsApi";

interface HashtagItem {
  Id: number;
  Name: string;
}

type HashtagType = "default" | "text";
type tagSelectorType = "select" | "filter";

export default defineComponent({
  name: "AppHashtag",
  components: {
    AppTooltip,
    AppIcon,
    AppResizableDropdown,
    AppTagSelector,
  },
  props: {
    items: {
      type: Array as PropType<HashtagItem[]>,
      default: () => [],
    },
    type: {
      type: String as PropType<HashtagType>,
      default: "default",
      validator: (value: string) => ["default", "text"].includes(value),
    },
    searchText: {
      type: String,
      default: "",
    },
    tooltipText: {
      type: String,
      default: "",
    },
    tooltipWidth: {
      type: Number,
      default: 186,
    },
    showTooltipOnHover: {
      type: Boolean,
      default: false,
    },
    showClear: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectorEnabled: {
      type: Boolean,
      default: false,
    },
    selectorValue: {
      type: Array as PropType<TagWithFrequency[]>,
      default: () => [],
    },
    entityType: {
      type: Number as PropType<TagEntityType>,
      default: TagEntityType.Folder,
    },
    entityId: {
      type: Number,
      default: 0,
    },
    tagSelectorMode: {
      type: String as PropType<tagSelectorType>,
      default: "filter",
      validator: (value: string) => ["select", "filter"].includes(value),
      // "select" | "filter" | undefined
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
    allowSelectGroup: {
      type: Boolean,
      default: true,
    },
    dropdownPosition: {
      type: String,
      default: "left",
    },
    dropdownMaxWidth: {
      type: Number,
      default: 640,
    },
    dropdownMaxHeight: {
      type: Number,
      default: 436,
    },
  },
  emits: ["click", "clear", "remove", "update:selectorValue", "create-tag"],
  setup(props, { emit }) {
    const showTooltip = ref(false);
    type ResizableDropdownRef = {
      close: () => void;
    };

    const dropdownRef = ref<ResizableDropdownRef | null>(null);
    const localSelectorValue = ref<TagWithFrequency[]>([
      ...props.selectorValue,
    ]);

    watch(
      () => props.selectorValue,
      (value) => {
        localSelectorValue.value = [...(value ?? [])];
      },
      { deep: true },
    );

    const clear = () => {
      emit("clear");
    };

    const removeTag = (item: HashtagItem, index: number) => {
      if (props.disabled) return;
      emit("remove", item, index);
    };

    const handleMouseEnter = () => {
      if (props.showTooltipOnHover && props.tooltipText) {
        showTooltip.value = true;
      }
    };

    const handleMouseLeave = () => {
      showTooltip.value = false;
    };

    const handleSelectorChange = (value: TagWithFrequency[]) => {
      if (props.disabled) return;
      localSelectorValue.value = value ?? [];
      emit("update:selectorValue", localSelectorValue.value);
    };

    const handleCreateTag = async (tagName: string) => {
      if (props.disabled) return;

      const normalizedName = tagName?.trim().toLowerCase();

      if (!normalizedName) return;

      if (!props.entityId) {
        console.error("Не передан entityId для добавления тега");
        return;
      }

      try {
        const updatedTags = await TagsApi.addTag({
          EditingTagId: 0,
          Name: normalizedName,
          EntityType: props.entityType,
          EntityId: Number(props.entityId),
        });

        if (Array.isArray(updatedTags)) {
          localSelectorValue.value = updatedTags.map((tag) => ({
            ...tag,
            Frequency: 0,
            FullName: tag.Name,
            Type: TagBaseType.Common,
          }));

          emit("create-tag", updatedTags);
        }

        dropdownRef.value?.close();
      } catch (e) {
        console.error("Ошибка при создании тега", e);
      }
    };

    return {
      showTooltip,
      dropdownRef,
      localSelectorValue,
      clear,
      removeTag,
      handleMouseEnter,
      handleMouseLeave,
      handleSelectorChange,
      handleCreateTag,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";

.hashtag-container {
  display: inline;
}

.button-link {
  display: inline;
  color: #979797 !important;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: filter 0.2s;
  white-space: normal;
  // overflow-wrap: anywhere;

  &__dropdown {
    display: inline-block;
    vertical-align: baseline;
  }

  &__wrapper {
    display: inline;
    white-space: normal;
  }

  &__hashtag {
    //   display: inline-flex;
    // vertical-align: baseline;
    // margin-right: 0;
    flex: 0 0 auto;
    color: #979797 !important;

    &:hover {
      color: $color-accent !important;
      transition: color 0.2s;
    }

    // &::after {
    //   // content: "\00A0";
    // }
  }

  &__cross {
    display: inline-flex;
    // vertical-align: baseline;
    // vertical-align: text-top;
    vertical-align: bottom;
    justify-content: center;
    width: 8px;
    height: 16px;
    font-size: 18px;
    line-height: 1;
    color: $color-divider !important;
    transition: color 0.2s;
    cursor: pointer;
    margin-left: 2px;
    opacity: 0.7;

    &:hover {
      color: $color-accent !important;
      opacity: 1;
    }

    &-disabled {
      color: #d2d2d2;
    }
  }

  &.hashtag-text-type {
    cursor: default;

    .button-link__hashtag {
      cursor: default;

      &:hover {
        color: #979797 !important;
      }
    }

    .tag-name:hover {
      color: #979797 !important;
    }
  }
}

.tag-item {
  display: inline-flex;
  align-items: baseline;
  vertical-align: baseline;
  max-width: 100%;
  min-width: 0;
  margin-right: 2px;
}

.tag-name {
  align-self: baseline;
  min-width: 0;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  // overflow-wrap: anywhere;
  overflow-wrap: normal;
  hyphens: none;
  color: #979797;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;

  .button-link:not(.hashtag-text-type) & {
    &:hover {
      color: $color-accent !important;
      transition: color 0.2s;
    }
  }

  &-disabled {
    color: #d2d2d2;
  }
}

// .tag-separator {
//   white-space: pre;
// }

:deep(.dropdown-wrapper) {
  display: inline-block;
  vertical-align: baseline;
}

:deep(.dropdown-activator) {
  display: inline-flex;
  vertical-align: baseline;
  align-items: baseline;
}
</style>
